import { useState, useEffect } from "react";
import { PageLayout } from "../components/Layout";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Users, 
  UserPlus,
  MessageSquare,
  CheckCircle,
  Clock,
  Star
} from "lucide-react";

const Mentorship = () => {
  const { user } = useAuth();
  const [mentors, setMentors] = useState([]);
  const [myMentorships, setMyMentorships] = useState({ as_mentee: [], as_mentor: [] });
  const [loading, setLoading] = useState(true);
  const [requestingMentor, setRequestingMentor] = useState(null);
  const [goals, setGoals] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [mentorsRes, mentorshipsRes] = await Promise.all([
        fetch(`${API}/mentors`, { credentials: 'include' }),
        fetch(`${API}/mentorships/me`, { credentials: 'include' })
      ]);

      if (mentorsRes.ok) setMentors(await mentorsRes.json());
      if (mentorshipsRes.ok) setMyMentorships(await mentorshipsRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const requestMentorship = async (mentorId) => {
    try {
      const response = await fetch(`${API}/mentorships`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mentor_id: mentorId, goals })
      });

      if (response.ok) {
        toast.success("Mentorship request sent!");
        setRequestingMentor(null);
        setGoals("");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send request");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const updateMentorshipStatus = async (mentorshipId, status) => {
    try {
      const response = await fetch(`${API}/mentorships/${mentorshipId}/status?status=${status}`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`Mentorship ${status}`);
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "badge-warning",
      active: "badge-success",
      completed: "badge-gold",
      cancelled: "badge-error"
    };
    return styles[status] || "badge-gold";
  };

  if (loading) {
    return (
      <PageLayout title="Mentorship">
        <div className="flex items-center justify-center py-20">
          <div className="spinner" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="Mentorship"
      subtitle="Connect with experienced professionals who can guide your career journey."
    >
      <Tabs defaultValue="find" className="space-y-8" data-testid="mentorship-page">
        <TabsList className="bg-surface-muted border border-slate-200">
          <TabsTrigger value="find" className="data-[state=active]:bg-brand-gold data-[state=active]:text-black">
            Find a Mentor
          </TabsTrigger>
          <TabsTrigger value="my" className="data-[state=active]:bg-brand-gold data-[state=active]:text-black">
            My Mentorships
          </TabsTrigger>
        </TabsList>

        {/* Find a Mentor */}
        <TabsContent value="find" className="space-y-6">
          {mentors.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mentors.map((mentor) => (
                <Card 
                  key={mentor.user_id} 
                  className="card-surface card-hover"
                  data-testid={`mentor-card-${mentor.user_id}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4 mb-4">
                      <Avatar className="h-14 w-14 border-2 border-[#FFD507]/30">
                        <AvatarImage src={mentor.picture} alt={mentor.name} />
                        <AvatarFallback className="bg-[#1E3A8A] text-slate-900 text-lg">
                          {mentor.name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="font-medium text-slate-900">{mentor.name}</h3>
                        <p className="text-sm text-slate-500">{mentor.email}</p>
                        <Badge className={`mt-1 ${mentor.availability === 'available' ? 'badge-success' : 'badge-warning'}`}>
                          {mentor.availability === 'available' ? 'Available' : 'Full'}
                        </Badge>
                      </div>
                    </div>

                    {mentor.bio && (
                      <p className="text-sm text-slate-500 mb-4 line-clamp-2">{mentor.bio}</p>
                    )}

                    {mentor.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {mentor.skills.slice(0, 3).map((skill, index) => (
                          <span 
                            key={index}
                            className="px-2 py-1 text-xs bg-[#1E3A8A]/30 text-[#3B82F6] rounded"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {mentor.current_mentees || 0} / 5 mentees
                      </span>
                    </div>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          className="w-full btn-primary"
                          disabled={mentor.availability !== 'available'}
                          onClick={() => setRequestingMentor(mentor)}
                          data-testid={`request-mentor-${mentor.user_id}`}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Request Mentorship
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-white border-slate-200">
                        <DialogHeader>
                          <DialogTitle className="text-slate-900">Request Mentorship with {mentor.name}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div>
                            <label className="text-sm text-slate-600 mb-2 block">
                              What are your goals for this mentorship?
                            </label>
                            <Textarea
                              value={goals}
                              onChange={(e) => setGoals(e.target.value)}
                              placeholder="Describe what you hope to achieve..."
                              className="input-base min-h-[100px]"
                              data-testid="mentorship-goals-input"
                            />
                          </div>
                          <Button 
                            onClick={() => requestMentorship(mentor.user_id)}
                            className="w-full btn-primary"
                            data-testid="submit-mentorship-request"
                          >
                            Send Request
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-surface">
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Mentors Available</h3>
                <p className="text-slate-500">Check back later for available mentors.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* My Mentorships */}
        <TabsContent value="my" className="space-y-6">
          {/* As Mentee */}
          <Card className="card-surface">
            <CardHeader>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <Star className="w-5 h-5 text-brand-navy" />
                My Mentors
              </CardTitle>
            </CardHeader>
            <CardContent>
              {myMentorships.as_mentee?.length > 0 ? (
                <div className="space-y-4">
                  {myMentorships.as_mentee.map((mentorship) => (
                    <div 
                      key={mentorship.mentorship_id}
                      className="flex items-center justify-between p-4 bg-surface-muted rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={mentorship.mentor?.picture} />
                          <AvatarFallback className="bg-[#1E3A8A] text-slate-900">
                            {mentorship.mentor?.name?.charAt(0)?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-slate-900">{mentorship.mentor?.name}</p>
                          <p className="text-sm text-slate-500">{mentorship.mentor?.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge className={getStatusBadge(mentorship.status)}>
                          {mentorship.status}
                        </Badge>
                        {mentorship.status === 'pending' && (
                          <span className="text-sm text-slate-500 flex items-center gap-1">
                            <Clock className="w-4 h-4" /> Awaiting approval
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">
                  You haven't requested any mentors yet. Find a mentor above!
                </p>
              )}
            </CardContent>
          </Card>

          {/* As Mentor (if user is a mentor) */}
          {user?.role === 'mentor' && (
            <Card className="card-surface">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-brand-navy" />
                  My Mentees
                </CardTitle>
              </CardHeader>
              <CardContent>
                {myMentorships.as_mentor?.length > 0 ? (
                  <div className="space-y-4">
                    {myMentorships.as_mentor.map((mentorship) => (
                      <div 
                        key={mentorship.mentorship_id}
                        className="flex items-center justify-between p-4 bg-surface-muted rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={mentorship.mentee?.picture} />
                            <AvatarFallback className="bg-[#1E3A8A] text-slate-900">
                              {mentorship.mentee?.name?.charAt(0)?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-slate-900">{mentorship.mentee?.name}</p>
                            <p className="text-sm text-slate-500">{mentorship.goals || 'No goals specified'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {mentorship.status === 'pending' ? (
                            <>
                              <Button
                                size="sm"
                                className="btn-primary"
                                onClick={() => updateMentorshipStatus(mentorship.mentorship_id, 'active')}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" /> Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="btn-outline"
                                onClick={() => updateMentorshipStatus(mentorship.mentorship_id, 'cancelled')}
                              >
                                Decline
                              </Button>
                            </>
                          ) : (
                            <Badge className={getStatusBadge(mentorship.status)}>
                              {mentorship.status}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">
                    No mentorship requests yet.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </PageLayout>
  );
};

export default Mentorship;
